import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Users from "@/assets/icons/users.svg?react";
import { SearchInputField } from "@/components/BgtForm";
import { BgtImageCard } from "@/components/BgtImageCard/BgtImageCard";
import { BgtCardList } from "@/components/BgtLayout/BgtCardList";
import { BgtEmptyPage } from "@/components/BgtLayout/BgtEmptyPage";
import { BgtPage } from "@/components/BgtLayout/BgtPage";
import { BgtPageContent } from "@/components/BgtLayout/BgtPageContent";
import BgtPageHeader from "@/components/BgtLayout/BgtPageHeader";
import { BgtText } from "@/components/BgtText/BgtText";
import { useFilteredList } from "@/hooks/useFilteredList";
import { usePermissions } from "@/hooks/usePermissions";
import { getPlayers } from "@/services/queries/players";
import { usePlayerModals } from "./-hooks/usePlayerModals";
import { usePlayersData } from "./-hooks/usePlayersData";
import { CreatePlayerModal } from "./-modals/CreatePlayerModal";

export const Route = createFileRoute("/players/")({
	component: RouteComponent,
	loader: ({ context: { queryClient } }) => {
		queryClient.prefetchQuery(getPlayers());
	},
});

function RouteComponent() {
	const { t } = useTranslation(["player", "common"]);
	const { canWrite } = usePermissions();
	const modals = usePlayerModals();
	const { players, isLoading } = usePlayersData();
	const { filterValue, setFilterValue, filtered: filteredPlayers } = useFilteredList(players, "name");

	if (isLoading) return null;

	if (players.length === 0) {
		return (
			<BgtEmptyPage
				header={t("common:players")}
				icon={Users}
				title={t("empty.title")}
				description={t("empty.description")}
				action={canWrite ? { label: t("new.button"), onClick: modals.createModal.show } : undefined}
			>
				<CreatePlayerModal open={modals.createModal.isOpen} close={modals.createModal.hide} />
			</BgtEmptyPage>
		);
	}

	return (
		<BgtPage>
			<BgtPageHeader
				header={t("common:players")}
				icon={Users}
				actions={
					canWrite
						? [
								{
									content: "player:new.button",
									variant: "primary",
									onClick: modals.createModal.show,
								},
							]
						: []
				}
			></BgtPageHeader>
			<BgtPageContent>
				<SearchInputField value={filterValue} onChange={(event) => setFilterValue(event.target.value)} />
				<BgtText size="3" className="pb-6 text-primary" weight="medium">
					{t("count", { count: filteredPlayers.length })}
				</BgtText>
				<BgtCardList>
					{filteredPlayers.map((x) => (
						<BgtImageCard key={x.id} title={x.name} image={x.image} link={`/players/${x.id}`} />
					))}
				</BgtCardList>
				<CreatePlayerModal open={modals.createModal.isOpen} close={modals.createModal.hide} />
			</BgtPageContent>
		</BgtPage>
	);
}
